import { useFocusEffect } from "expo-router";
import { useCallback, useRef, type RefObject } from "react";
import { AccessibilityInfo, type View } from "react-native";

import Logging from "$src/core/services/logging/logging";
import CustomPlatform from "$src/core/utils/platform/platform";

// tvOS always keeps a focused item. Android TV starts with none and waits for a key press.
const NEEDS_ENTRY_FOCUS = CustomPlatform.select({
  androidTV: true,
  default: false,
});

// Long enough for a slow first layout, short enough that a later layout cannot pull focus back.
const CLAIM_WINDOW_MS = 1500;

/**
 * Gives an Android TV screen a focused item, so the first remote press navigates
 * instead of claiming focus. Returns an `onLayout` handler that retries the claim
 * until the target view is laid out, or `undefined` on every other platform.
 */
const useTvEntryFocus = <T extends View>(ref: RefObject<T | null>) => {
  const isClaimWindowOpen = useRef(false);
  const isScreenReaderEnabled = useRef<boolean | null>(null);

  const claimEntryFocus = useCallback(() => {
    // Outside the window the user owns the focus, so a claim would move it away from them.
    if (!isClaimWindowOpen.current) return;
    // TalkBack moves accessibility focus itself, so a claim would fight the announcement.
    if (isScreenReaderEnabled.current !== false) return;

    ref.current?.requestTVFocus();
  }, [ref]);

  /* Claim focus each time the screen appears, because Android TV hands out none itself. */
  useFocusEffect(
    useCallback(() => {
      if (!NEEDS_ENTRY_FOCUS) return undefined;

      isClaimWindowOpen.current = true;
      isScreenReaderEnabled.current = null;

      const timeoutId = setTimeout(() => {
        isClaimWindowOpen.current = false;
      }, CLAIM_WINDOW_MS);

      AccessibilityInfo.isScreenReaderEnabled()
        .then((isEnabled) => {
          isScreenReaderEnabled.current = isEnabled;
          claimEntryFocus();
        })
        .catch((error) => {
          Logging.error("Failed to read the screen reader state", { error });
        });

      return () => {
        isClaimWindowOpen.current = false;
        clearTimeout(timeoutId);
      };
    }, [claimEntryFocus]),
  );

  return NEEDS_ENTRY_FOCUS ? claimEntryFocus : undefined;
};

export default useTvEntryFocus;
